import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowRight,
    Briefcase,
    CheckCircle2,
    Coins,
    DollarSign,
    Heart,
    HeartPulse,
    Home,
    LineChart,
    Loader2,
    PiggyBank,
    ScrollText,
    Search,
    Shield,
    Smile,
    Sparkles,
    TrendingUp,
    Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { submitOrganicLead } from "@/lib/api";
import TimezoneSelect from "@/components/TimezoneSelect";
import { Combobox } from "@/components/ui/combobox";
import { Slider } from "@/components/ui/slider";
import { detectTimezone } from "@/lib/timezones";
import { US_STATES } from "@/lib/usStates";

const DRAFT_KEY = "mm_lead_wizard_draft";

const PURPOSE_OPTIONS = [
    { key: "protect_family", icon: Shield, label: "Life insurance to protect my family" },
    { key: "final_expense",  icon: Heart, label: "Life insurance for final expenses" },
    { key: "iul",            icon: TrendingUp, label: "Indexed Universal Life (IUL)", sub: "Cash value growth + protection" },
    { key: "retirement",     icon: PiggyBank, label: "Retirement income / annuities" },
    { key: "health",         icon: HeartPulse, label: "Health insurance / Medicare" },
    { key: "dental_vision",  icon: Smile, label: "Dental or Vision" },
    { key: "reviewing",      icon: ScrollText, label: "Reviewing existing coverage" },
    { key: "not_sure",       icon: Search, label: "Not sure — help me explore" },
];

const CONTEXT_BY_PURPOSE = {
    protect_family: {
        question: "Who's counting on you financially?",
        multi: true,
        options: ["Spouse", "Children", "Parents", "Business partners", "Just me for now"],
    },
    final_expense: {
        question: "Is this for you or a loved one?",
        options: ["Me", "A loved one", "Both"],
    },
    retirement: {
        question: "What's your retirement timeline?",
        options: ["<5 years", "5–10 years", "10–20 years", "20+ years"],
    },
    health: {
        question: "Which applies?",
        options: ["On Medicare", "Approaching 65", "Individual health", "Family health"],
    },
    dental_vision: {
        question: "Individual or family plan?",
        options: ["Individual", "Family"],
    },
    reviewing: {
        question: "What kind of policy are you reviewing?",
        options: ["Life", "Health", "Annuity", "Other"],
    },
};

// ----- IUL sub-qualifier options -----------------------------------------

const IUL_FINANCIAL_GOALS = [
    { key: "grow_long_term_cash_value", icon: TrendingUp,  label: "Grow Long Term Cash Value" },
    { key: "borrow_against_policy",     icon: Wallet,      label: "Borrow Against My Policy" },
    { key: "protect_family",            icon: Shield,      label: "Protect My Family" },
    { key: "retirement_income",         icon: PiggyBank,   label: "Retirement Income" },
];

const IUL_CURRENT_INVESTMENTS = [
    { key: "k401",                     icon: Briefcase, label: "401(k)" },
    { key: "ira",                      icon: PiggyBank, label: "IRA" },
    { key: "cash_savings",             icon: Coins,     label: "Cash Savings" },
    { key: "active_trading",           icon: LineChart, label: "Active Trading" },
    { key: "self_directed_brokerage",  icon: Wallet,    label: "Self Directed Brokerage Account" },
];

const IUL_MONTHLY_SAVINGS = [
    { key: "lt_300",   icon: DollarSign, label: "Less than $300" },
    { key: "300_500",  icon: DollarSign, label: "$300 - $500" },
    { key: "500_1000", icon: DollarSign, label: "$500 - $1,000" },
    { key: "gt_1000",  icon: DollarSign, label: "More than $1,000" },
];

const IUL_FUNDING_ABILITY = [
    { key: "can_fund",    icon: CheckCircle2, label: "Yes, I can fund a plan" },
    { key: "seeking_aid", icon: Search,       label: "No, I'm looking for aid" },
];

const BUDGET_BANDS = [
    { v: "<50k",    l: "Less than $50k" },
    { v: "50-250k", l: "$50k – $250k" },
    { v: "250-500k",l: "$250k – $500k" },
    { v: "500k-1M", l: "$500k – $1M" },
    { v: "1M+",     l: "$1M+" },
    { v: "not_sure",l: "Not sure — I'd like guidance" },
];

const URGENCY = [
    { v: "asap",        l: "Right away" },
    { v: "within_30",   l: "Within 30 days" },
    { v: "1-3_months",  l: "1–3 months" },
    { v: "exploring",   l: "Just exploring" },
];

const HEALTH = [
    { v: "excellent", l: "Excellent" },
    { v: "good",      l: "Good" },
    { v: "average",   l: "Average" },
    { v: "concerns",  l: "Some concerns" },
    { v: "later",     l: "Prefer to discuss on our call" },
];

const CONTACT_METHODS = ["Phone", "Email", "Text"];
const CONTACT_TIMES   = ["Morning", "Midday", "Afternoon", "Evening"];

// IUL is a life product — buckets it with the "requires coverage band" flow.
const LIFE_PATHS = new Set(["protect_family", "final_expense", "iul"]);
const IUL_PATH = "iul";

const AGE_MIN = 18;
const AGE_MAX = 65;
const AGE_DEFAULT = 35;

function mapProductInterest(purpose, context) {
    if (purpose === "protect_family") return "Term";
    if (purpose === "final_expense") return "Final Expense";
    if (purpose === "iul") return "IUL";
    if (purpose === "retirement") return "Annuities";
    if (purpose === "health") {
        if (context === "On Medicare" || context === "Approaching 65") return "Medicare";
        return "Health";
    }
    if (purpose === "dental_vision") return "Dental/Vision";
    if (purpose === "reviewing") {
        const map = { Life: "Term", Health: "Health", Annuity: "Annuities", Other: null };
        return map[context] || null;
    }
    return null;
}

function detectTimezoneLocal() {
    return detectTimezone();
}

/**
 * Compute the ordered list of step keys for the current purpose.
 *   - "iul" flow: purpose → 4 IUL substeps → budget → urgency → age → health → contact → notes
 *   - other life flows: purpose → context → budget → urgency → age → health → contact → notes
 *   - non-life flows: purpose → context (or skipped for not_sure) → urgency → age → health → contact → notes
 * Step 1 is always `purpose`. `contact` step carries the State picker (Phase 10).
 */
function flowFor(purpose) {
    if (purpose === IUL_PATH) {
        return [
            "purpose",
            "iul_goal",
            "iul_investments",
            "iul_savings",
            "iul_funding",
            "budget",
            "urgency",
            "age",
            "health",
            "contact",
            "notes",
        ];
    }
    if (purpose === "not_sure") {
        return ["purpose", "context", "urgency", "age", "health", "contact", "notes"];
    }
    const includeBudget = LIFE_PATHS.has(purpose);
    return [
        "purpose",
        "context",
        ...(includeBudget ? ["budget"] : []),
        "urgency",
        "age",
        "health",
        "contact",
        "notes",
    ];
}

const INITIAL = {
    step: 1,
    purpose: "",
    context: "",
    iul_financial_goal: "",
    iul_current_investments: [],
    iul_monthly_savings: "",
    iul_funding_ability: "",
    budget_band: "",
    urgency: "",
    age: AGE_DEFAULT,
    health: "",
    contact: {
        name: "", email: "", phone: "",
        method: "Phone", time: "Morning", zip: "",
        state: "",
        timezone: detectTimezoneLocal(),
    },
    notes: "",
    consent: false,
};

function useDraft() {
    const [state, setState] = useState(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                return {
                    ...INITIAL,
                    ...parsed,
                    // Coerce/clamp age for anyone with a stale draft.
                    age: clampAge(parsed.age ?? AGE_DEFAULT),
                    contact: { ...INITIAL.contact, ...(parsed.contact || {}) },
                    iul_current_investments: Array.isArray(parsed.iul_current_investments)
                        ? parsed.iul_current_investments
                        : [],
                };
            }
        } catch { /* noop */ }
        return INITIAL;
    });
    useEffect(() => {
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(state)); } catch { /* noop */ }
    }, [state]);
    return [state, setState];
}

function clampAge(n) {
    const v = Math.round(Number(n));
    if (!Number.isFinite(v)) return AGE_DEFAULT;
    if (v < AGE_MIN) return AGE_MIN;
    if (v > AGE_MAX) return AGE_MAX;
    return v;
}

export default function LeadWizard() {
    const [state, setState] = useDraft();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const flow = useMemo(() => flowFor(state.purpose), [state.purpose]);
    const total = flow.length;
    const currentIdx = Math.min(state.step - 1, total - 1);
    const currentKey = flow[currentIdx] || "purpose";
    const progress = Math.round((currentIdx / (total - 1 || 1)) * 100);

    const set = (patch) => setState((s) => ({ ...s, ...patch }));
    const setContact = (patch) =>
        setState((s) => ({ ...s, contact: { ...s.contact, ...patch } }));

    const canAdvance = () => {
        switch (currentKey) {
            case "purpose": return !!state.purpose;
            case "context": {
                if (state.purpose === "not_sure") return true;
                const cfg = CONTEXT_BY_PURPOSE[state.purpose];
                if (!cfg) return true;
                if (cfg.multi) return Array.isArray(state.context) && state.context.length > 0;
                return !!state.context;
            }
            case "iul_goal":        return !!state.iul_financial_goal;
            case "iul_investments": return Array.isArray(state.iul_current_investments) && state.iul_current_investments.length > 0;
            case "iul_savings":     return !!state.iul_monthly_savings;
            case "iul_funding":     return !!state.iul_funding_ability;
            case "budget":          return !!state.budget_band;
            case "urgency":         return !!state.urgency;
            case "age":             return Number.isFinite(state.age) && state.age >= AGE_MIN && state.age <= AGE_MAX;
            case "health":          return true; // optional
            case "contact": {
                const c = state.contact;
                if (!c.name.trim()) return false;
                if (!/^\S+@\S+\.\S+$/.test(c.email)) return false;
                if ((c.phone || "").replace(/\D+/g, "").length < 10) return false;
                if (!c.state) return false;
                return true;
            }
            case "notes":  return state.consent;
            default:       return false;
        }
    };

    const next = () => { if (canAdvance()) setState((s) => ({ ...s, step: Math.min(total, s.step + 1) })); };
    const back = () => { setState((s) => ({ ...s, step: Math.max(1, s.step - 1) })); };

    // Whenever purpose changes, snap step back to the beginning of the new flow
    // if the current step index no longer fits. Prevents landing in an out-of-flow step.
    useEffect(() => {
        if (state.step > total) setState((s) => ({ ...s, step: total }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.purpose, total]);

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const product_interest = mapProductInterest(state.purpose, state.context);
            const isIul = state.purpose === IUL_PATH;
            const payload = {
                purpose: state.purpose,
                context: state.purpose === IUL_PATH ? null : (state.context || null),
                budget_band: LIFE_PATHS.has(state.purpose) ? (state.budget_band || null) : null,
                urgency: state.urgency || null,
                age: clampAge(state.age),
                health: state.health || null,
                contact: {
                    name: state.contact.name.trim(),
                    email: state.contact.email.trim(),
                    phone: state.contact.phone.trim(),
                    method: state.contact.method,
                    time: state.contact.time,
                    zip: state.contact.zip.trim() || null,
                    state: (state.contact.state || "").toUpperCase(),
                },
                notes: (state.notes || "").trim() || null,
                consent: state.consent,
                product_interest,
                timezone: state.contact.timezone || detectTimezoneLocal(),
                ...(isIul ? {
                    iul_financial_goal: state.iul_financial_goal || null,
                    iul_current_investments: state.iul_current_investments || [],
                    iul_monthly_savings: state.iul_monthly_savings || null,
                    iul_funding_ability: state.iul_funding_ability || null,
                } : {}),
            };
            await submitOrganicLead(payload);
            try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
            setSubmitted(true);
        } catch (err) {
            const detail = err?.response?.data;
            toast.error(
                (detail && (detail.detail || (detail.fields && detail.fields[0]?.message))) ||
                "Something went wrong — please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <section id="lead-form" className="mm-section bg-white" data-testid="lead-wizard-success">
                <div className="mm-container">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mm-card mx-auto max-w-2xl p-10 text-center"
                    >
                        <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                            <CheckCircle2 size={30} />
                        </div>
                        <h2 className="mm-display text-3xl text-mm-navy">
                            Thank you — your advisor will reach out personally.
                        </h2>
                        <p className="mt-3 text-mm-muted">
                            A licensed Moving Mountains advisor reviews every submission. We&apos;ll be in touch via your preferred contact method.
                        </p>
                        <p className="mt-5 mm-disclaimer">
                            This experience is powered by advanced AI technology. Moving Mountains does not guarantee approval, rates, or outcomes.
                        </p>
                    </motion.div>
                </div>
            </section>
        );
    }

    const isLastStep = currentIdx === total - 1;

    return (
        <section id="lead-form" className="mm-section bg-white" data-testid="lead-wizard">
            <div className="mm-container">
                <div className="mx-auto max-w-2xl">
                    <div className="mm-eyebrow mb-3">Free, no-obligation review</div>
                    <h2 className="mm-display text-4xl sm:text-5xl text-mm-navy">
                        Tell us what you&apos;re looking for.
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-mm-muted leading-relaxed">
                        A few quick questions — nothing personal. We&apos;ll hand the details to a licensed Moving Mountains advisor who will personally reach out.
                    </p>

                    <div className="mt-8 mm-card p-5 sm:p-8" data-testid="lead-wizard-card">
                        {/* Progress */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between text-xs text-mm-muted">
                                <span data-testid="wizard-step-counter">
                                    Step {currentIdx + 1} of {total}
                                </span>
                                <span>{progress}%</span>
                            </div>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-mm-line">
                                <motion.div
                                    className="h-full bg-gradient-to-r from-mm-navy via-mm-blue to-mm-sky"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                />
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentKey + "-" + currentIdx}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.28, ease: "easeOut" }}
                            >
                                {currentKey === "purpose"         && <PurposeStep         state={state} set={set} />}
                                {currentKey === "context"         && <ContextStep         state={state} set={set} />}
                                {currentKey === "iul_goal"        && <IulGoalStep         state={state} set={set} />}
                                {currentKey === "iul_investments" && <IulInvestmentsStep  state={state} set={set} />}
                                {currentKey === "iul_savings"     && <IulSavingsStep      state={state} set={set} />}
                                {currentKey === "iul_funding"     && <IulFundingStep      state={state} set={set} />}
                                {currentKey === "budget"          && <BudgetStep          state={state} set={set} />}
                                {currentKey === "urgency"         && <UrgencyStep         state={state} set={set} />}
                                {currentKey === "age"             && <AgeStep             state={state} set={set} />}
                                {currentKey === "health"          && <HealthStep          state={state} set={set} />}
                                {currentKey === "contact"         && <ContactStep         state={state} setContact={setContact} />}
                                {currentKey === "notes"           && <NotesStep           state={state} set={set} />}
                            </motion.div>
                        </AnimatePresence>

                        {/* Nav */}
                        <div className="mt-8 flex flex-col-reverse items-stretch gap-3 border-t border-mm-line pt-5 sm:flex-row sm:items-center sm:justify-between">
                            <button
                                type="button"
                                onClick={back}
                                disabled={currentIdx === 0}
                                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-mm-line bg-white px-4 py-3 text-sm font-medium text-mm-navy hover:bg-mm-bg disabled:cursor-not-allowed disabled:opacity-40 sm:py-2"
                                data-testid="wizard-back"
                            >
                                <ArrowLeft size={14} /> Back
                            </button>
                            {!isLastStep ? (
                                <button
                                    type="button"
                                    onClick={next}
                                    disabled={!canAdvance()}
                                    className="mm-btn-primary w-full justify-center !py-3 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:!py-2.5"
                                    data-testid="wizard-next"
                                >
                                    Next <ArrowRight size={14} />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={!canAdvance() || submitting}
                                    className="mm-btn-primary w-full justify-center !py-3 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:!py-2.5"
                                    data-testid="wizard-submit"
                                >
                                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                    Get My Free Review
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="mt-4 mm-disclaimer text-center">
                        We do not guarantee approval, rates, or outcomes. We do not sell your data.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* -------- Step components -------- */

function StepHeader({ title, subtitle }) {
    return (
        <div className="mb-6">
            <h3 className="mm-display text-2xl text-mm-navy sm:text-3xl">{title}</h3>
            {subtitle && <p className="mt-2 text-sm text-mm-muted">{subtitle}</p>}
        </div>
    );
}

function CardOptionGrid({ options, value, onChange, multi = false, testidPrefix }) {
    const values = multi ? (Array.isArray(value) ? value : []) : value;
    const toggle = (k) => {
        if (!multi) { onChange(k); return; }
        if (values.includes(k)) onChange(values.filter((v) => v !== k));
        else onChange([...values, k]);
    };
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            {options.map((opt) => {
                const k = opt.key ?? opt;
                const label = opt.label ?? opt;
                const sub = opt.sub;
                const Icon = opt.icon;
                const active = multi ? values.includes(k) : values === k;
                return (
                    <button
                        key={k}
                        type="button"
                        onClick={() => toggle(k)}
                        className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
                            active
                                ? "border-mm-navy bg-mm-navy text-white shadow-md"
                                : "border-mm-line bg-white text-mm-ink hover:border-mm-navy/40"
                        }`}
                        data-testid={`${testidPrefix}-${k}`}
                    >
                        {Icon ? (
                            <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${active ? "bg-white/15" : "bg-mm-bg"}`}>
                                <Icon size={18} className={active ? "text-white" : "text-mm-blue"} />
                            </span>
                        ) : null}
                        <span className="flex flex-col">
                            <span className="text-sm font-medium leading-snug">{label}</span>
                            {sub && (
                                <span className={`mt-0.5 text-[11px] leading-snug ${active ? "text-white/75" : "text-mm-muted"}`}>
                                    {sub}
                                </span>
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function PillRow({ options, value, onChange, testidPrefix }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => {
                const v = o.v ?? o;
                const l = o.l ?? o;
                const active = value === v;
                return (
                    <button
                        key={v}
                        type="button"
                        onClick={() => onChange(v)}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                            active
                                ? "border-mm-navy bg-mm-navy text-white"
                                : "border-mm-line bg-white text-mm-ink hover:border-mm-navy/40"
                        }`}
                        data-testid={`${testidPrefix}-${v}`}
                    >
                        {l}
                    </button>
                );
            })}
        </div>
    );
}

function PurposeStep({ state, set }) {
    return (
        <div data-testid="wizard-step-purpose">
            <StepHeader title="What's bringing you here today?" subtitle="Pick the option that fits closest — you can adjust later." />
            <CardOptionGrid
                options={PURPOSE_OPTIONS}
                value={state.purpose}
                onChange={(v) => set({
                    purpose: v,
                    // Reset branch-specific state so back-forward doesn't leak.
                    context: "",
                    iul_financial_goal: "",
                    iul_current_investments: [],
                    iul_monthly_savings: "",
                    iul_funding_ability: "",
                    step: 2,
                })}
                testidPrefix="wizard-purpose"
            />
        </div>
    );
}

function ContextStep({ state, set }) {
    if (state.purpose === "not_sure") {
        return (
            <div data-testid="wizard-step-context">
                <StepHeader title="That's completely fine." subtitle="We'll help you explore. Tap Next to continue." />
            </div>
        );
    }
    const cfg = CONTEXT_BY_PURPOSE[state.purpose];
    if (!cfg) return null;
    return (
        <div data-testid="wizard-step-context">
            <StepHeader title={cfg.question} subtitle={cfg.multi ? "Select all that apply." : "Pick one."} />
            {cfg.multi ? (
                <CardOptionGrid
                    options={cfg.options.map((o) => ({ key: o, label: o }))}
                    value={state.context}
                    onChange={(v) => set({ context: v })}
                    multi
                    testidPrefix="wizard-context"
                />
            ) : (
                <PillRow
                    options={cfg.options.map((o) => ({ v: o, l: o }))}
                    value={state.context}
                    onChange={(v) => set({ context: v })}
                    testidPrefix="wizard-context"
                />
            )}
        </div>
    );
}

function IulGoalStep({ state, set }) {
    return (
        <div data-testid="wizard-step-iul-goal">
            <StepHeader title="What is your financial goal?" subtitle="Pick the outcome that matters most — this shapes how we design your policy." />
            <CardOptionGrid
                options={IUL_FINANCIAL_GOALS}
                value={state.iul_financial_goal}
                onChange={(v) => set({ iul_financial_goal: v })}
                testidPrefix="wizard-iul-goal"
            />
        </div>
    );
}

function IulInvestmentsStep({ state, set }) {
    return (
        <div data-testid="wizard-step-iul-investments">
            <StepHeader title="What are your current investments?" subtitle="Select all that apply." />
            <CardOptionGrid
                options={IUL_CURRENT_INVESTMENTS}
                value={state.iul_current_investments}
                onChange={(v) => set({ iul_current_investments: v })}
                multi
                testidPrefix="wizard-iul-investments"
            />
        </div>
    );
}

function IulSavingsStep({ state, set }) {
    return (
        <div data-testid="wizard-step-iul-savings">
            <StepHeader title="How much do you set aside in savings each month?" />
            <CardOptionGrid
                options={IUL_MONTHLY_SAVINGS}
                value={state.iul_monthly_savings}
                onChange={(v) => set({ iul_monthly_savings: v })}
                testidPrefix="wizard-iul-savings"
            />
        </div>
    );
}

function IulFundingStep({ state, set }) {
    return (
        <div data-testid="wizard-step-iul-funding">
            <StepHeader title="Are you able to fund an IUL monthly for growth potential?" subtitle="Be honest — your advisor will right-size the plan either way." />
            <CardOptionGrid
                options={IUL_FUNDING_ABILITY}
                value={state.iul_funding_ability}
                onChange={(v) => set({ iul_funding_ability: v })}
                testidPrefix="wizard-iul-funding"
            />
        </div>
    );
}

function BudgetStep({ state, set }) {
    return (
        <div data-testid="wizard-step-budget">
            <StepHeader title="How much coverage feels right?" subtitle="A rough ballpark is fine — your advisor will right-size it." />
            <PillRow
                options={BUDGET_BANDS}
                value={state.budget_band}
                onChange={(v) => set({ budget_band: v })}
                testidPrefix="wizard-budget"
            />
        </div>
    );
}

function UrgencyStep({ state, set }) {
    return (
        <div data-testid="wizard-step-urgency">
            <StepHeader title="How soon are you looking to have coverage in place?" />
            <PillRow options={URGENCY} value={state.urgency} onChange={(v) => set({ urgency: v })} testidPrefix="wizard-urgency" />
        </div>
    );
}

function AgeStep({ state, set }) {
    const age = clampAge(state.age);
    return (
        <div data-testid="wizard-step-age">
            <StepHeader title="How old are you?" subtitle="Drag the slider to your age." />
            <div className="mm-card border-mm-line/70 p-6">
                <div className="flex items-baseline justify-center gap-2">
                    <span
                        className="mm-display text-6xl tabular-nums text-mm-navy"
                        aria-live="polite"
                        data-testid="wizard-age-value"
                    >
                        {age}
                    </span>
                    <span className="text-sm font-medium uppercase tracking-wider text-mm-muted">
                        yrs
                    </span>
                </div>
                <div className="mt-6">
                    <Slider
                        min={AGE_MIN}
                        max={AGE_MAX}
                        step={1}
                        value={[age]}
                        onValueChange={(vals) => set({ age: clampAge(vals?.[0] ?? AGE_DEFAULT) })}
                        aria-label="Age"
                        aria-valuemin={AGE_MIN}
                        aria-valuemax={AGE_MAX}
                        aria-valuenow={age}
                        data-testid="wizard-age-slider"
                    />
                    <div className="mt-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-mm-muted">
                        <span>{AGE_MIN}</span>
                        <span>{AGE_MAX}</span>
                    </div>
                </div>
                <p className="mt-5 text-center text-xs text-mm-muted">
                    We currently write policies for clients age {AGE_MIN} to {AGE_MAX}.
                </p>
            </div>
        </div>
    );
}

function HealthStep({ state, set }) {
    return (
        <div data-testid="wizard-step-health">
            <StepHeader title="How would you describe your overall health?" subtitle="Optional — and fully up to you whether to answer now or on our call." />
            <PillRow
                options={HEALTH}
                value={state.health}
                onChange={(v) => set({ health: v })}
                testidPrefix="wizard-health"
            />
        </div>
    );
}

function ContactStep({ state, setContact }) {
    const c = state.contact;
    const stateSections = [
        { heading: "United States", options: US_STATES },
    ];
    return (
        <div data-testid="wizard-step-contact">
            <StepHeader title="Almost done — how should we reach you?" />
            <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name *">
                    <input className="mm-input" value={c.name} onChange={(e) => setContact({ name: e.target.value })} autoComplete="name" data-testid="wizard-contact-name" />
                </Field>
                <Field label="Email *">
                    <input type="email" inputMode="email" className="mm-input" value={c.email} onChange={(e) => setContact({ email: e.target.value })} autoComplete="email" data-testid="wizard-contact-email" />
                </Field>
                <Field label="Phone *">
                    <input type="tel" inputMode="tel" className="mm-input" value={c.phone} onChange={(e) => setContact({ phone: e.target.value })} autoComplete="tel" data-testid="wizard-contact-phone" />
                </Field>
                <Field label="Zip code (optional)">
                    <input inputMode="numeric" pattern="[0-9]*" className="mm-input" value={c.zip} onChange={(e) => setContact({ zip: e.target.value })} autoComplete="postal-code" data-testid="wizard-contact-zip" />
                </Field>
                <Field label="Best contact method">
                    <div className="flex gap-2">
                        {CONTACT_METHODS.map((m) => (
                            <button key={m} type="button" onClick={() => setContact({ method: m })}
                                className={`flex-1 rounded-full border px-3 py-2 text-sm transition ${c.method === m ? "border-mm-navy bg-mm-navy text-white" : "border-mm-line bg-white text-mm-ink hover:border-mm-navy/40"}`}
                                data-testid={`wizard-contact-method-${m.toLowerCase()}`}>
                                {m}
                            </button>
                        ))}
                    </div>
                </Field>
                <Field label="Best time (local)">
                    <div className="flex flex-wrap gap-2">
                        {CONTACT_TIMES.map((t) => (
                            <button key={t} type="button" onClick={() => setContact({ time: t })}
                                className={`flex-1 rounded-full border px-3 py-2 text-sm transition ${c.time === t ? "border-mm-navy bg-mm-navy text-white" : "border-mm-line bg-white text-mm-ink hover:border-mm-navy/40"}`}
                                data-testid={`wizard-contact-time-${t.toLowerCase()}`}>
                                {t}
                            </button>
                        ))}
                    </div>
                </Field>
                <div className="sm:col-span-2">
                    <Field label="Timezone">
                        <TimezoneSelect
                            value={c.timezone || detectTimezoneLocal()}
                            onChange={(v) => setContact({ timezone: v })}
                            testid="wizard-contact-tz"
                        />
                    </Field>
                </div>
                <div className="sm:col-span-2">
                    <Field label="What state do you live in? *">
                        <Combobox
                            value={c.state || ""}
                            onChange={(v) => setContact({ state: (v || "").toUpperCase() })}
                            sections={stateSections}
                            placeholder="Select your state"
                            searchPlaceholder="Search states (e.g. Florida, TX)…"
                            emptyLabel="No matching state."
                            testid="wizard-contact-state"
                        />
                    </Field>
                </div>
            </div>
        </div>
    );
}

function NotesStep({ state, set }) {
    return (
        <div data-testid="wizard-step-notes">
            <StepHeader title="Anything you'd like your advisor to know?" subtitle="Optional — up to 500 characters." />
            <textarea
                className="mm-input resize-y"
                rows={5}
                value={state.notes}
                onChange={(e) => set({ notes: e.target.value.slice(0, 500) })}
                placeholder="Share any context that would help us prepare for your call."
                data-testid="wizard-notes"
            />
            <div className="mt-1 text-right text-[11px] text-mm-muted">
                {state.notes.length}/500
            </div>
            <label className="mt-5 flex items-start gap-3 rounded-xl border border-mm-line bg-white p-3 text-sm text-mm-ink">
                <input
                    type="checkbox"
                    checked={state.consent}
                    onChange={(e) => set({ consent: e.target.checked })}
                    className="mt-1 h-4 w-4 accent-mm-navy"
                    data-testid="wizard-consent"
                />
                <span>
                    I understand Moving Mountains will contact me about this inquiry and does not sell my data.
                </span>
            </label>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[12px] font-medium uppercase tracking-wider text-mm-muted">{label}</span>
            {children}
        </label>
    );
}
